import { useContext, useEffect, useState } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { shallowEqual, useSelector } from 'react-redux'
import _ from 'lodash'

import { PRIVILEGES, ERROR_403_PATH, COOKIE_AUTH_TOKEN } from '@/constants'
import { isProduction, isCompositorEnabled, getCookie } from '@/utils'
import { ModalContext } from '@/contexts/ModalContext'
import * as views from '@/views'
import ModalError from '@/components/modals/ModalError/ModalError'
import NavigateTo from '@/components/NavigateTo'
import StranglerGuard from './StranglerGuard'
import Toggles from '@domain/toggles/Toggles'

// The browser keeps the auth cookie until its own max-age, but the JWT inside can already be
// expired. Decode the `exp` and require it to be in the future, so a present-but-expired session
// is NOT treated as authenticated (otherwise '/' would briefly redirect to '/projects' before the
// refresh resolves and fails). Mirrors AuthContext's getTokenExpiryMs.
const hasValidAuthToken = () => {
	const token = getCookie(COOKIE_AUTH_TOKEN)
	if (!token) return false
	try {
		const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
		return typeof exp === 'number' && exp * 1000 > Date.now()
	} catch {
		return false
	}
}

const PublicRoute = (props) => {
	const { component: Component, ...routeProps } = props

	return (
		<Route {...routeProps}>
			<Component />
		</Route>
	)
}

const ProtectedRoute = (props) => {
	const {
		redirectPath: redirectPathProp = '/',
		component: Component,
		allowed: allowedProp = [],
		...routeProps
	} = props

	const allowed = typeof allowedProp === 'string' ? [allowedProp] : allowedProp
	const redirectTo = {
		pathname: redirectPathProp
	}

	const logged = useSelector(state => state.auth.logged, shallowEqual)
	const accountDetails = useSelector(state => state.auth.account, shallowEqual)
	const [isAccessible, setIsAccessible] = useState(null)

	useEffect(() => {
		if (logged === false) {
			setIsAccessible(false)
		} else if (logged && allowed.length) {
			if (!accountDetails) {
				return
			}

			allowed.some((privilege) => accountDetails.privileges?.includes(PRIVILEGES[privilege]))
				? setIsAccessible(true)
				: setIsAccessible(false)
		} else if (logged) {
			setIsAccessible(true)
		} else {
			setIsAccessible(null)
		}
	}, [logged, accountDetails])

	useEffect(() => {
		if (isAccessible) {
			if (!isProduction()) {
				console.log(`User allowed to view route: ${routeProps.path}`)
			}
		} else if (isAccessible === false) {
			if (!isProduction()) {
				logged
					? console.log(`User NOT allowed to view route: '${routeProps.path}'. Redirecting to '${ERROR_403_PATH}'…`)
					: console.log(`User NOT allowed to view route: '${routeProps.path}'. Redirecting to '${redirectPathProp}'…`)
			}
		}
	}, [isAccessible])

	return (
		<Route
			{...routeProps}
			render={(props) => {
				if (isAccessible) {
					return <Component {...props} />
				} else if (isAccessible === false) {
					return logged ? <Redirect to={ERROR_403_PATH} /> : <Redirect to={redirectTo} />
				} else {
					return null
				}
			}}
		/>
	)
}

const MainRouter = () => {
	const { modalMount } = useContext(ModalContext)

	useEffect(() => {
		if (typeof window !== 'undefined' && window.dataLayer) {
			// Extract dl_english_path from existing HTML element (replace selector with actual class)
			let dlEnglishPath = window.location.pathname
			const existingElement = document.querySelector('.dl-english-path') // Replace with actual class selector
			if (existingElement) {
				dlEnglishPath = existingElement.textContent || existingElement.getAttribute('data-english-path') || window.location.pathname
				// Remove the element to avoid future inconsistencies
				existingElement.remove()
			}

			window.dataLayer.push({
				'dl_page_type': 'Professional Area',
				'dl_english_path': dlEnglishPath
			})
		}
	}, [])

	const accountDetails = useSelector(state => state.auth.account, shallowEqual)
	const logged = useSelector(state => state.auth.logged, shallowEqual)
	const error = useSelector(state => state.error, shallowEqual)

	// `logged` is tri-state: true (logged in), false (after logout), null (unknown). A fresh
	// anonymous visitor who never logged in stays null forever (AuthContext only flips it on a
	// restore/refresh/logout). The two redirects below MUST NOT be strict inverses driven by the
	// same flag, or they ping-pong ('/projects' → '/' → '/projects') while auth is resolving on
	// login. Instead each uses its own SETTLED condition, with a deliberate "hold" zone (logged
	// still null + a present-but-not-yet-valid cookie) where neither fires until `logged` resolves:
	//  - authedForHome: only leave '/' for '/projects' once we're confident it's an auth session
	//    (logged true, or null with a still-valid token). An expired cookie is NOT valid → stays.
	//  - anonAtProjects: only leave '/projects' for '/' when it's a settled anonymous (logged out,
	//    or null with NO auth cookie at all = fresh visitor). A present cookie during login means
	//    "hold", so a just-logged-in user is never bounced off '/projects'.
	const newProfesionalAreaOn = Toggles.isEnabled('newProfesionalArea')
	const authedForHome = logged === true || (logged === null && hasValidAuthToken())
	const anonAtProjects = logged === false || (logged === null && !getCookie(COOKIE_AUTH_TOKEN))

	useEffect(() => {
		if (error.details) {
			setTimeout(() => {
				modalMount(<ModalError disableBackdropClick />)
			}, 100)
		}
	}, [error.details])

	const compositorEnabled = isCompositorEnabled(accountDetails?.email)

	const getNewCompositorPreconfigView = () => {
		if (Toggles.isEnabled('newCompositors')) {
			return views.ToolkitListConfiguratorPreconfigView
		} else {
			return views.MiniCompositor
		}
	}

	const getToolkitListConfiguratorCreateView = () => {
		return views.ToolkitListConfiguratorCreateView
	}

	const getBundleListConfiguratorCreateView = () => {
		return views.BundleListConfiguratorCreateView
	}

	const getRegularProductConfiguratorCreateView = () => {
		return views.RegularProductConfiguratorCreateView
	}

	const getToolkitListConfiguratorEditView = () => {
		return views.ToolkitListConfiguratorEditView
	}

	const getRegularProductConfiguratorEditView = () => {
		return views.RegularProductConfiguratorEditView
	}

	return (
		<>
			<NavigateTo />
			<StranglerGuard>
			<Switch>
				{/* With newProfesionalArea ON, '/' is migrated (professional-area/Next owns it) and the
				    project list lives at /projects. An AUTHENTICATED '/' redirects to /projects; an
				    ANONYMOUS '/' is rendered here only long enough for the StranglerGuard to hand it
				    off to Next (so after logout you land on the NEW home, never the legacy list).
				    With the toggle OFF, '/' keeps rendering the legacy list. */}
				{newProfesionalAreaOn && authedForHome
					? <Redirect exact from="/" to="/projects" />
					: <PublicRoute path="/" component={views.ProjectListView} exact />}
				{/* /projects is NOT migrated (always served by eproject). With the toggle ON an
				    ANONYMOUS visitor must not see the list — send them to '/' (which Next then serves
				    as the new home). Authenticated users, and everyone when the toggle is OFF, see the
				    list. */}
				{newProfesionalAreaOn && anonAtProjects
					? <Redirect exact from="/projects" to="/" />
					: <PublicRoute path="/projects" component={views.ProjectListView} exact />}
				<PublicRoute path="/professional-area" component={views.ProjectListView} exact />
				{Toggles.isEnabled("collectionFiltersTest") && <PublicRoute path="/collection-filter-demo" component={views.CollectionFilter} exact />}

				{/* `to` is an object so the query survives: `?intent=new-project` is set by the
				    "Create new project" button and has to reach the configurator. A string `to`
				    would drop it. */}
				<Route path="/create-project" exact render={({ location }) => (
					<Redirect to={{ pathname: '/create-project/select-collection', search: location.search }} />
				)} />
				<PublicRoute path="/create-project/select-collection" component={views.CollectionList} exact />
				{/* DV-6625: the decision tree of a collection application picked in the professional
				    area's collections selector (Next). Before the selector routes: `:subFamilyId`
				    below has no `exact` and would take these too. */}
				<PublicRoute path="/create-project/select-collection/:subFamilyId/:applicationLocationId" component={views.CollectionDecisionTreeView} exact />
				<PublicRoute path="/create-project/select-collection/:subFamilyId" component={views.CollectionList} />

				<PublicRoute path="/add-product/:subFamilyId" component={views.AddProduct} exact />
				<PublicRoute path="/add-product/:subFamilyId/:applicationLocationId" component={views.AddProduct} exact />
				<PublicRoute path="/add-product/:subFamilyId/:applicationLocationId/:model" component={views.AddProduct} exact />

				<PublicRoute path="/add-product-v2/:subFamilyId/:applicationLocationId/:model" component={views.AddProductV2} exact />

				<PublicRoute path="/preconfigs-list/:subFamilyId" component={views.PreconfigListView} exact />
				<PublicRoute path="/preconfigs-list/:subFamilyId/:applicationLocationId" component={views.PreconfigListView} exact />

				<PublicRoute path="/add-preconfig/:preconfigId" component={views.AddPreconfig} exact />

				<Redirect from="/project/:projectId/add-product" to="/project/:projectId/add-product/select-collection" exact />
				<ProtectedRoute
					path="/project/:projectId/add-product/select-collection"
					allowed="PROJECT_VIEW"
					component={views.CollectionList}
					exact
				/>
				<ProtectedRoute
					path="/project/:projectId/add-product/select-collection/:subFamilyId/:applicationLocationId"
					allowed="PROJECT_VIEW"
					component={views.CollectionDecisionTreeView}
					exact
				/>
				<ProtectedRoute
					path="/project/:projectId/add-product/select-collection/:subFamilyId"
					allowed="PROJECT_VIEW"
					component={views.CollectionList}
				/>

				<ProtectedRoute path="/project/:projectId" allowed="PROJECT_VIEW" component={views.ProjectPageView} exact />
				{/* Always mounted: the migrated project detail (professional-area) links here for the
					budget request, since that screen is not migrated. The toggle still decides what
					THIS app's own footer does; it no longer decides whether the screen exists. */}
				<ProtectedRoute path="/project/:projectId/request-budget" component={views.RequestBudget} exact />
				<ProtectedRoute path="/project/:projectId/usa-request-budget" component={views.RequestBudgetUsa} exact />
				<ProtectedRoute path="/project/:projectId/generate-budget" component={views.GenerateBudget} exact />
				<ProtectedRoute path="/project/:projectId/registration-usa" component={views.ProjectRegistrationUsa} exact />
				<ProtectedRoute
					path="/project/:projectId/budget-quotation-usa"
					allowed="PROJECT_REGISTRATION"
					component={views.BudgetQuotationUsa}
					exact
				/>

				<PublicRoute
					path="/project/:projectId/product/:productId/express-configurator"
					component={views.ProductExpressConfiguratorView}
					exact />

				<PublicRoute path="/new-product/ToolkitListConfigurator" component={getToolkitListConfiguratorCreateView()} exact />
				<PublicRoute path="/new-product/BundleListConfigurator" component={getBundleListConfiguratorCreateView()} exact />
				<PublicRoute path="/new-product/RegularConfigurator" component={getRegularProductConfiguratorCreateView()} exact />
				<PublicRoute path="/project/:projectId/add-product/ToolkitListConfigurator" component={getToolkitListConfiguratorCreateView()} exact />
				<PublicRoute path="/project/:projectId/add-product/BundleListConfigurator" component={getBundleListConfiguratorCreateView()} exact />
				<PublicRoute path="/project/:projectId/add-product/RegularConfigurator" component={getRegularProductConfiguratorCreateView()} exact />

				<PublicRoute
					path="/project/:projectId/product/:productId/ToolkitListConfigurator"
					component={getToolkitListConfiguratorEditView()}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/RegularConfigurator"
					component={getRegularProductConfiguratorEditView()}
					exact
				/>

				<PublicRoute path="/new-product/crea-configurator" component={views.ProductCreaConfiguratorView} exact />
				<PublicRoute path="/new-product/crea-configurator/:model" component={views.ProductCreaConfiguratorView} exact />
				<PublicRoute
					path="/project/:projectId/add-product/crea-configurator"
					component={views.ProductCreaConfiguratorView}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/crea-configurator/:mode"
					component={views.ProductCreaConfiguratorView}
					exact
				/>

				<Redirect
					from="/sticks-nodrawing-configurator"
					to={{
						pathname: '/new-product/sticks-nodrawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute path="/new-product/sticks-nodrawing-configurator" component={views.SticksNodrawingConfigurator} exact />
				<PublicRoute
					path="/project/:projectId/add-product/sticks-nodrawing-configurator"
					component={views.SticksNodrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/sticks-nodrawing-configurator/:mode"
					component={views.SticksNodrawingConfigurator}
					exact
				/>

				<Redirect
					from="/sticks-drawing-configurator"
					to={{
						pathname: '/new-product/sticks-drawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute path="/new-product/sticks-drawing-configurator" component={views.SticksDrawingConfigurator} exact />
				<PublicRoute
					path="/project/:projectId/add-product/sticks-drawing-configurator"
					component={views.SticksDrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/sticks-drawing-configurator/:mode"
					component={views.SticksDrawingConfigurator}
					exact
				/>

				<Redirect
					from="/plusminus-nodrawing-configurator"
					to={{
						pathname: '/new-product/plusminus-nodrawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute
					path="/new-product/plusminus-nodrawing-configurator"
					component={views.PlusminusNodrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/add-product/plusminus-nodrawing-configurator"
					component={views.PlusminusNodrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/plusminus-nodrawing-configurator/:mode"
					component={views.PlusminusNodrawingConfigurator}
					exact
				/>
				<Redirect
					from="/plusminus-drawing-configurator"
					to={{
						pathname: '/new-product/plusminus-drawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute
					path="/new-product/plusminus-drawing-configurator"
					component={views.PlusminusDrawingConfiguratorContainer}
					exact
				/>
				<PublicRoute
					path="/plusminus-drawing-configurator/product/:hash"
					component={views.PlusminusDrawingConfiguratorWithHash}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/add-product/plusminus-drawing-configurator"
					component={views.PlusminusDrawingConfiguratorContainer}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/plusminus-drawing-configurator/:mode"
					component={views.PlusminusDrawingConfiguratorContainer}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/export"
					component={views.Export3dContainer}
					exact
				/>
				<ProtectedRoute
					path="/validate/:projectId/:productId"
					component={views.ValidationView}
					exact
				/>

				<ProtectedRoute path="/orders" allowed={"ORDER_VIEW"} component={views.OrderList} />
				<ProtectedRoute path="/order/:orderId" allowed="ORDER_VIEW" component={views.OrderDetails} />
				{Toggles.isEnabled('getUserByCrmIdV3')
					? <ProtectedRoute path="/project/:projectId/new-order" allowed="ORDER_ADD" component={views.PlaceOrder} />
					: <PublicRoute path="/project/:projectId/new-order" allowed="ORDER_VIEW" component={views.PlaceOrder} />}

				<ProtectedRoute
					path="/project/:projectId/downloads"
					allowed="PROJECT_DOWNLOADS"
					component={views.ProjectDownloads}
				/>
				<ProtectedRoute
					path="/project/:projectId/technical-downloads"
					allowed="PROJECT_DOWNLOADS"
					component={views.ProjectTechnicalDownloads}
				/>
				<ProtectedRoute
					path="/project/:projectId/export-3d"
					allowed="PROJECT_DOWNLOADS"
					component={views.Export3DList}
				/>

				<PublicRoute path="/services/download" component={views.ServiceDownload} />

				<ProtectedRoute path="/my-profile" component={views.UserProfile} exact />
				<ProtectedRoute path="/users-management" allowed="COMPANY_MANAGE_USER" component={views.UsersManagement} exact />
				<ProtectedRoute path="/invoices" allowed={"INVOICE_VIEW"} component={views.Invoices} exact />
				<PublicRoute path="/password-reset" component={views.PasswordReset} exact />
				<Redirect from="/new-register" to="en/new-register" exact />
				<PublicRoute path="/:language/new-register" component={views.NewRegister} exact />
				<PublicRoute path="/sandbox" component={views.Sandbox} exact />

				<PublicRoute path="/compositor/v2" component={views.CompositorV2} exact />
				{!!compositorEnabled && <PublicRoute path="/studio" component={views.Studio} exact />}
				{!!compositorEnabled && <PublicRoute path="/compositor" component={views.Compositor} exact />}
				{!!compositorEnabled && <PublicRoute path="/compositor/generatePreviews" component={views.GeneratePreviews} exact />}
				{!!compositorEnabled && <PublicRoute path="/compositor/generatePdf" component={views.GeneratePdf} exact />}
				{!!compositorEnabled && <PublicRoute path="/technicalinfo" component={views.TechnicalInformation} exact />}
				{!!compositorEnabled && <PublicRoute path="/productsheet" component={views.ProductSheetView} exact />}
				{Toggles.isEnabled("exportProductInfoAndDownloads") && <PublicRoute path="/product-info-download" component={views.ProductInfoAndDownloadsView} exact />}
				<PublicRoute path="/product-admin" component={views.ProductAdmin} exact />
				<PublicRoute path="/product-admin/compose" component={views.ProductAdminCompositor} exact />
				<PublicRoute path="/product-admin/EditRule" component={views.ProductAdminEditRule} exact />
				<PublicRoute path="/preview/compose" component={views.ProductAdminPreview} exact />
				<PublicRoute path="/compositor/_demo" component={views.Compositor} exact />
				<PublicRoute path="/preconfigs/ToolkitListConfigurator" component={getNewCompositorPreconfigView()} exact />
				<PublicRoute path={ERROR_403_PATH} component={views.NotAllowedView} exact />

				<PublicRoute component={views.NotFoundView} />

			</Switch>
			</StranglerGuard>
		</>
	)
}

export default MainRouter
